/**
 * EditManager — orchestrates the editing lifecycle for a single element.
 * Manages contentEditable, MutationObserver, compound history operations,
 * and coordinates with ToolbarManager.
 */

import type { MessageBridge } from './message-bridge'
import type { HistoryManager } from './history'
import type { ToolbarManager } from './toolbar'
import type { FeatureConfig } from './config'
import { CONFIG, hasAnyStyleFeature } from './config'
import { getElementPath, log } from './utils'

export class EditManager {
  private currentElement: HTMLElement | null = null

  private observer: MutationObserver | null = null

  private debounceTimer: ReturnType<typeof setTimeout> | null = null

  private hasChangesDebounceTimer: ReturnType<typeof setTimeout> | null = null

  private originalContent = ''

  private toolbar: ToolbarManager | null

  private bridge: MessageBridge

  private history: HistoryManager

  private featureConfig: FeatureConfig

  constructor(
    toolbar: ToolbarManager | null,
    bridge: MessageBridge,
    history: HistoryManager,
    featureConfig: FeatureConfig,
  ) {
    this.toolbar = toolbar
    this.bridge = bridge
    this.history = history
    this.featureConfig = featureConfig
  }

  startEditing(element: HTMLElement): void {
    if (this.currentElement && this.currentElement !== element) {
      this.stopEditing()
    }

    if (this.currentElement === element) {
      log('元素已在编辑中，跳过', { element: element.tagName })
      return
    }

    this.originalContent = element.innerHTML
    this.currentElement = element

    element.classList.remove('musk-editor-hover')
    element.classList.add('musk-editor-editing')

    if (this.featureConfig.textEdit) {
      element.contentEditable = 'true'

      if (element.tagName === 'BUTTON') {
        if (!element.hasAttribute('tabindex')) {
          element.setAttribute('tabindex', '0')
        }
        (element.style as any).webkitUserModify = 'read-write';
        (element.style as any).webkitAppearance = 'none';
        (element.style as any).webkitUserSelect = 'text'
        element.style.userSelect = 'text'
        element.style.caretColor = 'auto'
      }

      element.focus()

      this.observer = new MutationObserver(() => {
        this.handleContentChange()
        this.checkAndNotifyHasChanges()
      })

      this.observer.observe(element, {
        childList: true,
        subtree: true,
        characterData: true,
      })
    }

    if (this.toolbar && hasAnyStyleFeature(this.featureConfig)) {
      this.toolbar.show(element)
    }

    this.bridge.send('EDIT_START', {
      element: getElementPath(element),
      originalContent: this.originalContent,
    })

    log('开始编辑 (只保存原始内容，不记录历史)', {
      element: element.tagName,
      path: getElementPath(element),
    })
  }

  stopEditing(): void {
    if (!this.currentElement) return

    const element = this.currentElement
    const newContent = element.innerHTML

    if (this.featureConfig.textEdit) {
      element.contentEditable = 'false'
    }

    if (element.tagName === 'BUTTON') {
      (element.style as any).webkitUserModify = '';
      (element.style as any).webkitAppearance = '';
      (element.style as any).webkitUserSelect = ''
      element.style.userSelect = ''
      element.style.caretColor = ''
    }

    element.classList.remove('musk-editor-editing')

    if (this.observer) {
      this.observer.disconnect()
    }

    if (this.hasChangesDebounceTimer) clearTimeout(this.hasChangesDebounceTimer)

    const contentChanged = this.featureConfig.textEdit && this.originalContent !== newContent
    const styleChanges = this.toolbar ? this.toolbar.getStyleDiff(element) : []
    const hasAnyChange = contentChanged || styleChanges.length > 0

    if (hasAnyChange) {
      const elementId = element.getAttribute('data-musk-element-id')
      this.history.addOperation({
        type: 'compound',
        elementId: elementId || '',
        oldText: this.originalContent,
        newText: newContent,
        styleChanges,
      })
      log('记录复合操作', {
        elementId,
        textChanged: contentChanged,
        styleChangesCount: styleChanges.length,
      })
    }

    this.bridge.send('EDIT_END', {
      element: getElementPath(element),
      elementId: element.getAttribute('data-musk-element-id') || '',
      originalContent: this.originalContent,
      newContent,
      changed: hasAnyChange,
    })

    if (this.toolbar) {
      this.toolbar.clearEditSession()
      this.toolbar.hide()
    }

    log('结束编辑', {
      element: element.tagName,
      changed: hasAnyChange,
    })

    this.currentElement = null
    this.originalContent = ''
  }

  private handleContentChange(): void {
    if (this.debounceTimer) clearTimeout(this.debounceTimer)
    this.debounceTimer = setTimeout(() => {
      this.bridge.send('CONTENT_CHANGED', {
        content: document.body.innerHTML,
        element: this.currentElement ? getElementPath(this.currentElement) : '',
      })
    }, CONFIG.DEBOUNCE_DELAY)
  }

  private checkAndNotifyHasChanges(): void {
    if (!this.currentElement) return

    if (this.hasChangesDebounceTimer) clearTimeout(this.hasChangesDebounceTimer)
    this.hasChangesDebounceTimer = setTimeout(() => {
      if (!this.currentElement) return
      const currentContent = this.currentElement.innerHTML
      const hasChanged = this.originalContent !== currentContent

      this.bridge.send('HAS_CHANGES_UPDATED', {
        hasChanges: hasChanged,
        element: getElementPath(this.currentElement),
      })

      log('实时检测内容变化', {
        hasChanged,
        originalLength: this.originalContent.length,
        currentLength: currentContent.length,
      })
    }, 300)
  }

  isEditing(): boolean {
    return this.currentElement !== null
  }

  getCurrentElement(): HTMLElement | null {
    return this.currentElement
  }
}
